import {
  Checkbox,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import { ReactNode, useEffect, useState } from 'react';
import { DASHBOARD_COLORS } from './statusDashboardStyles';

const MENU_ITEM_HEIGHT = 54;
const MENU_LIST_PADDING = 8;

const useStyles = makeStyles(() => ({
  paper: {
    width: 280,
    display: 'flex',
    flexDirection: 'column',
  },
  search: {
    padding: '12px 12px 4px',
  },
  list: {
    // the same height as the select filters of the app grids, plus room for the search
    maxHeight: MENU_ITEM_HEIGHT * 5 + MENU_LIST_PADDING * 2,
    overflowY: 'auto',
  },
  menuItem: {
    maxHeight: MENU_ITEM_HEIGHT,
  },
  empty: {
    padding: '12px 16px',
    fontSize: 13,
    color: DASHBOARD_COLORS.muted,
  },
}));

export interface CheckboxMenuOption {
  value: string;
  label: string;
}

interface CheckboxMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  options: CheckboxMenuOption[];
  selected: string[];
  onToggle: (value: string) => void;
  // Shows a field that narrows the options to the ones whose name or email has what is typed
  searchable?: boolean;
  // First item of the list, such as "All senders"; hidden while searching
  firstItem?: ReactNode;
}

// Menu of options with checkboxes, like the select filters of the app grids, that can also be searched
export const CheckboxMenu = ({
  anchorEl,
  onClose,
  options,
  selected,
  onToggle,
  searchable,
  firstItem,
}: CheckboxMenuProps) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const open = !!anchorEl;

  // Every time the menu opens it shows all the options again
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const text = search.trim().toLowerCase();
  const visible = text
    ? options.filter(
        option =>
          option.label.toLowerCase().includes(text) ||
          option.value.toLowerCase().includes(text)
      )
    : options;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      classes={{ paper: classes.paper }}
    >
      {searchable && (
        <div className={classes.search}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}
      <MenuList className={classes.list} autoFocusItem={!searchable}>
        {!text && firstItem}
        {visible.map(option => (
          <MenuItem
            key={option.value}
            classes={{ root: classes.menuItem }}
            onClick={() => onToggle(option.value)}
          >
            <Checkbox checked={selected.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
        {visible.length === 0 && (
          <div className={classes.empty}>No matches</div>
        )}
      </MenuList>
    </Popover>
  );
};
